// File: src/components/Prescriptions/Sidebar.jsx
import React, { useState, useContext, useMemo, useEffect } from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import {
  LayoutDashboard,
  TrendingUp,
  Zap,
  ChevronDown,
  ChevronRight,
  Users,
  Eye,
  ArrowDown,
  MousePointer,
  BarChart3,
  Globe,
  Settings,
  Home,
  LogOut,
  User as UserIcon,
  FileText,
} from 'lucide-react';
import { AuthContext } from '../../context/AuthContext';
import './Sidebar.css';
import FeedbackWidget from '../ui/FeedbackWidget';


// ----- Nav config with 3 Strategic Buckets -----
const navigationConfig = [
  { id: 'home', label: 'Homepage', path: '/home', icon: Home },
  {
    id: 'dashboard',
    label: 'Dashboard',
    path: '/dashboard',
    icon: LayoutDashboard,
    children: [
      // BUCKET 1: Customer Intelligence
      {
        id: 'customer-intel',
        label: 'Customer Intelligence',
        path: '/business',
        icon: Users,
        description: 'Who buys, why they buy, what makes them stick',
        children: [
          { id: 'customer-dashboard', label: 'Customer Dashboard', path: '/customerdashboard', icon: Eye, description: 'Segments, LTV, retention & behavior' },
          { id: 'journeys', label: 'Visitor Journeys', path: '/sessions', icon: Users, description: 'Session replay & journey analysis' },
        ],
      },
      // BUCKET 2: Experience & Conversion
      {
        id: 'experience',
        label: 'Experience & Conversion',
        path: '/performance',
        icon: Zap,
        description: 'Where friction happens, how to fix it',
        children: [
          { id: 'funnel', label: 'Conversion Funnel', path: '/dropoffs', icon: ArrowDown, description: 'Drop-offs & cart abandonment' },
          { id: 'ux-insights', label: 'UX Insights', path: '/UXInsightsPage', icon: MousePointer, description: 'Rage clicks & heatmaps' },
        ],
      },
      // BUCKET 3: Growth & Strategy
      {
        id: 'growth',
        label: 'Growth & Strategy',
        path: '/facts',
        icon: TrendingUp,
        description: "What's working, what to do next",
        children: [
          { id: 'performance-dash', label: 'Performance Dashboard', path: '/performanceDashboard', icon: BarChart3, description: 'Revenue trends & traffic sources' },
          { id: 'social-intel', label: 'Social Intelligence', path: '/customer-voice', icon: Globe, description: 'Brand mentions & sentiment' },
        ],
      },
    ],
  },
  { id: 'reports', label: 'Reports', path: '/reports', icon: FileText },
  { id: 'divider', type: 'divider' },
  { id: 'settings', label: 'Settings', path: '/settings', icon: Settings },
];

// Helper function to find parent items for a given path
function findParentIds(items, targetPath, parentIds = []) {
  for (const item of items) {
    if (item.path === targetPath) {
      return [...parentIds, item.id];
    }
    if (item.children) {
      const result = findParentIds(item.children, targetPath, [...parentIds, item.id]);
      if (result) return result;
    }
  }
    return null;
}

// ----- Component -----
export default function Sidebar({ collapsed: controlledCollapsed, onToggle }) {
  const location = useLocation();
  const navigate = useNavigate();
  const { token, logout } = useContext(AuthContext);

  // Mobile state
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 900);
  const [mobileOpen, setMobileOpen] = useState(false);

  // Handle window resize
  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth <= 900;
      setIsMobile(mobile);
      // Auto-close mobile menu when switching to desktop
      if (!mobile && mobileOpen) {
        setMobileOpen(false);
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [mobileOpen]);

  // Close on escape key (mobile)
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && mobileOpen) {
        setMobileOpen(false);
      }
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [mobileOpen]);

  // Prevent body scroll when mobile menu is open
  useEffect(() => {
    if (mobileOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [mobileOpen]);

  // Close mobile menu on navigation
  useEffect(() => {
    if (isMobile && mobileOpen) {
      setMobileOpen(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  // Controlled/uncontrolled collapse support
  // IMPORTANT: On mobile, collapsed is always false - we use mobileOpen instead
  const [internalCollapsed, setInternalCollapsed] = useState(false);
  const collapsed = isMobile ? false : (controlledCollapsed ?? internalCollapsed);

  // Handle sidebar toggle (hamburger click)
  const handleToggle = () => {
    if (onToggle) onToggle();
    else setInternalCollapsed((v) => !v);
  };

  // Function to open the sidebar, handling both controlled and uncontrolled scenarios
  const openSidebar = () => {
    if (collapsed) {
      if (controlledCollapsed === undefined) {
        setInternalCollapsed(false);
      } else if (onToggle) {
        onToggle(false);
      }
    }
  };

  // Function to collapse the sidebar
  const collapseSidebar = () => {
    if (!collapsed) {
      if (controlledCollapsed === undefined) {
        setInternalCollapsed(true);
      } else if (onToggle) {
        onToggle(true);
      }
    }
  };

  // Expand if user clicks anywhere inside the sidebar while it's collapsed (DESKTOP ONLY)
  const handleSidebarClickCapture = (e) => {
    // Skip all this logic on mobile - mobile uses mobileOpen state instead
    if (isMobile) return;
    
    if (e.target.closest('.nav-toggle')) {
      return;
    }

    if (e.target.closest('.feedback-widget-container, .feedback-modal-overlay')) {
      return;
    }

    if (collapsed) {
      openSidebar();
      return;
    }

    const clickedInteractive = e.target.closest(
      '.nav-item, .nav-parent-header, a, button, .nav-children'
    );

    if (!clickedInteractive) {
      collapseSidebar();
    }
  };


  // Optional a11y: expand on Enter/Space when focused (DESKTOP ONLY)
  const handleSidebarKeyDown = (e) => {
    if (isMobile) return;
    if (!collapsed) return;
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      openSidebar();
    }
  };


  // Auto-expand sections that contain the current route
  const [expanded, setExpanded] = useState(() => new Set());

  // Update expanded state when location changes
  useEffect(() => {
    const newExpanded = new Set();
    
    const parentIds = findParentIds(navigationConfig, location.pathname);
    
    if (parentIds) {
      parentIds.forEach(id => newExpanded.add(id));
    }
    
    navigationConfig.forEach(item => {
      if (item.path === location.pathname && item.children) {
        newExpanded.add(item.id);
      }
      if (item.children) {
        item.children.forEach(child => {
          if (child.path === location.pathname && child.children) {
            newExpanded.add(item.id);
            newExpanded.add(child.id);
          }
        });
      }
    });
    
    setExpanded(newExpanded);
    
  }, [location.pathname]);

  const toggleSection = (id) => {
    setExpanded((prev) => {
      const ns = new Set(prev);
      ns.has(id) ? ns.delete(id) : ns.add(id);
      return ns;
    });
  };

  const isActiveRoute = (path) =>
    location.pathname === path || location.pathname.startsWith(path + '/');

  const handleLogout = () => {
    if (token && typeof logout === 'function') {
      logout();
      navigate('/', { replace: true });
    }
  };

  const brand = useMemo(
    () => (
      <div className="nav-brand">
        <img src={`${process.env.PUBLIC_URL}/AdobeStock_133441382.png`} alt="BaoBrain Logo" className="nav-logo" />
        <span className="nav-brand-title">BaoBrain</span>
      </div>
    ),
    []
  );

  const isExactActive = (path) => location.pathname === path;

  const renderNavItem = (item, isChild = false) => {
    if (item.type === 'divider') {
      return <div key={item.id} className="nav-divider" />;
    }

    const Icon = item.icon;
    const hasChildren = Array.isArray(item.children) && item.children.length > 0;
    const expandedNow = expanded.has(item.id);
    const active = item.path && isActiveRoute(item.path);
    
    const activeExact = item.path && isExactActive(item.path);
    const childActive = hasChildren && item.children.some((c) => 
      (c.path && isActiveRoute(c.path)) || 
      (c.children && c.children.some(gc => gc.path && isActiveRoute(gc.path)))
    );

    // Leaf nav item (no children)
    if (item.path && !hasChildren) {
      return (
        <NavLink
          key={item.id}
          to={item.path}
          className={`nav-item ${active ? 'active' : ''} ${collapsed ? 'collapsed' : ''} ${
            isChild ? 'child' : ''
          }`}
          title={collapsed ? item.label : undefined}
          onClick={openSidebar}
        >
          {Icon && <Icon size={18} className="nav-icon" />}
          {!collapsed && (
            <>
              <span className="nav-label">{item.label}</span>
              {item.description && !isChild && (
                <span className="nav-description">{item.description}</span>
              )}
            </>
          )}
        </NavLink>
      );
    }

    // Parent nav item with children
    return (
      <div
        key={item.id}
        className={`nav-item has-children ${activeExact ? 'active' : ''} ${childActive ? 'open' : ''} ${collapsed ? 'collapsed' : ''} ${isChild ? 'child' : ''}`}
        title={collapsed ? item.label : undefined}
      >
        <div
          className="nav-parent-header"
          onClick={() => {
            openSidebar();
            if (hasChildren) {
              toggleSection(item.id);
            }
            if (item.path) {
              navigate(item.path);
            }
          }}
          role="button"
        >
          {Icon && <Icon size={18} className="nav-icon" />}
          {!collapsed && (
            <>
              <span className="nav-label">{item.label}</span>
              <span className="nav-chevron">
                {expandedNow ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
              </span>
            </>
          )}
        </div>

        {hasChildren && expandedNow && !collapsed && (
          <div className="nav-children">
            {item.children.map((child) => renderNavItem(child, true))}
          </div>
        )}
      </div>
    );
  };

  return (
    <>
      {/* Mobile hamburger button - fixed position, shows on mobile */}
      {isMobile && (
        <button
          className="mobile-hamburger-btn"
          onClick={() => setMobileOpen(true)}
          onTouchEnd={(e) => {
            e.preventDefault();
            setMobileOpen(true);
          }}
          aria-label="Open navigation menu"
          type="button"
        >
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
            <path
              d="M3 6H21M3 12H21M3 18H21"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
            />
          </svg>
          <span className="mobile-hamburger-label">Menu</span>
        </button>
      )}

      {/* Mobile overlay */}
      {isMobile && mobileOpen && (
        <div 
          className="sidebar-overlay visible" 
          onClick={() => setMobileOpen(false)}
          onTouchEnd={(e) => {
            e.preventDefault();
            setMobileOpen(false);
          }}
          aria-hidden="true"
        />
      )}

      <nav
        className={`navigation-sidebar sidebar-fixed ${!isMobile && collapsed ? 'is-collapsed' : ''} ${isMobile ? 'is-mobile' : ''} ${isMobile && mobileOpen ? 'mobile-open' : ''}`}
        onClickCapture={handleSidebarClickCapture}
        onKeyDown={handleSidebarKeyDown}
        tabIndex={!isMobile && collapsed ? 0 : -1}
      >
        <div className="nav-header">
          <button
            className="nav-toggle"
            onClick={() => {
              if (isMobile) {
                setMobileOpen(false);
              } else {
                handleToggle();
              }
            }}
            onTouchEnd={(e) => {
              if (isMobile) {
                e.preventDefault();
                setMobileOpen(false);
              }
            }}
            aria-label={isMobile ? 'Close navigation' : (collapsed ? 'Expand navigation' : 'Collapse navigation')}
            type="button"
          >
            {isMobile && mobileOpen ? (
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                <path
                  d="M5 5L15 15M15 5L5 15"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                />
              </svg>
            ) : (
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                <path
                  d="M3 5H17M3 10H17M3 15H17"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                />
              </svg>
            )}
          </button>
          {!collapsed && brand}
        </div>

        <div className="nav-content">
          {navigationConfig.map((item) => renderNavItem(item))}
        </div>

        <div className="nav-footer">
          {/* Feedback Widget */}
          <FeedbackWidget siteId={1} sessionId={null} />
          
          {token ? (
            <div className="nav-user-section">
              <div className="nav-user-info">
                <UserIcon size={18} className="nav-icon" />
                {!collapsed && <span className="nav-user-label">Signed In</span>}
              </div>
              <button className="nav-logout-btn" onClick={handleLogout} type="button" title="Sign Out">
                <LogOut size={18} className="nav-icon" />
                {!collapsed && <span className="nav-label">Logout</span>}
              </button>
            </div>
          ) : (
            <div className="nav-auth-section">
              <button
                className="nav-auth-btn"
                onClick={() => navigate('/login')}
                type="button"
                title="Sign In"
              >
                <UserIcon size={18} className="nav-icon" />
                {!collapsed && <span className="nav-label">Sign In</span>}
              </button>
            </div>
          )}
        </div>
      </nav>
    </>
  );
}